import { supabase } from '@/integrations/supabase/client';

const ACTIVE_RUN_KEY = 'medd-network-qualification-active';
const ACTIVE_RUN_TTL_MS = 2 * 60 * 60 * 1000;

export interface ActiveQualificationRun {
  runId: string;
  agentId: string;
  activatedAt: number;
  initialiseStartedAt?: number;
  connectionClosedAt?: number;
  reconnectCount?: number;
  lastUserTranscriptAt?: number;
}

function readActiveRun(): ActiveQualificationRun | null {
  try {
    const raw = localStorage.getItem(ACTIVE_RUN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveQualificationRun;
    if (!parsed.runId || !parsed.agentId || Date.now() - parsed.activatedAt > ACTIVE_RUN_TTL_MS) {
      localStorage.removeItem(ACTIVE_RUN_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveActiveRun(run: ActiveQualificationRun | null) {
  try {
    if (run) localStorage.setItem(ACTIVE_RUN_KEY, JSON.stringify(run));
    else localStorage.removeItem(ACTIVE_RUN_KEY);
  } catch {
    // Qualification telemetry must never interrupt a simulation.
  }
}

async function updateRun(runId: string, values: Record<string, unknown>) {
  const { error } = await (supabase as any)
    .from('network_qualification_runs')
    .update({ ...values, updated_at: new Date().toISOString() })
    .eq('id', runId);
  if (error) console.warn('[Network qualification] telemetry update failed:', error.message);
}

export function activateQualificationRun(runId: string, agentId: string) {
  saveActiveRun({ runId, agentId, activatedAt: Date.now() });
}

export function beginQualificationInitialisation(): ActiveQualificationRun | null {
  const active = readActiveRun();
  if (!active) return null;
  const next = { ...active, initialiseStartedAt: Date.now() };
  saveActiveRun(next);
  void updateRun(active.runId, { status: 'session_active' });
  return next;
}

export function recordQualificationToken(run: ActiveQualificationRun | null) {
  if (!run?.initialiseStartedAt) return;
  void updateRun(run.runId, { token_request_ms: Date.now() - run.initialiseStartedAt });
}

export function recordQualificationConnection(run: ActiveQualificationRun | null) {
  if (!run) return;
  const active = readActiveRun() || run;
  const values: Record<string, unknown> = {};
  if (active.initialiseStartedAt) {
    values.connection_established_ms = Date.now() - active.initialiseStartedAt;
  }
  if (active.connectionClosedAt) {
    active.reconnectCount = (active.reconnectCount || 0) + 1;
    values.reconnect_count = active.reconnectCount;
    delete active.connectionClosedAt;
    saveActiveRun(active);
  }
  void updateRun(active.runId, values);
}

export function recordQualificationStream(run: ActiveQualificationRun | null) {
  if (!run?.initialiseStartedAt) return;
  void updateRun(run.runId, { stream_start_ms: Date.now() - run.initialiseStartedAt });
}

export async function recordQualificationDisconnect(reason: string) {
  const active = readActiveRun();
  if (!active) return;
  active.connectionClosedAt = Date.now();
  saveActiveRun(active);
  const { data } = await (supabase as any)
    .from('network_qualification_runs')
    .select('disconnect_count')
    .eq('id', active.runId)
    .maybeSingle();
  void updateRun(active.runId, {
    disconnect_count: Number(data?.disconnect_count || 0) + 1,
    last_disconnect_reason: reason.slice(0, 500),
  });
}

function percentile(values: number[], fraction: number) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.ceil(sorted.length * fraction) - 1)];
}

export async function recordQualificationTranscript(role: 'user' | 'assistant') {
  const active = readActiveRun();
  if (!active) return;
  if (role === 'user') {
    active.lastUserTranscriptAt = Date.now();
    saveActiveRun(active);
    return;
  }
  if (!active.lastUserTranscriptAt) return;
  const elapsed = Date.now() - active.lastUserTranscriptAt;
  delete active.lastUserTranscriptAt;
  saveActiveRun(active);
  const { data } = await (supabase as any)
    .from('network_qualification_runs')
    .select('transcript_response_samples_ms')
    .eq('id', active.runId)
    .maybeSingle();
  const samples = [...(Array.isArray(data?.transcript_response_samples_ms) ? data.transcript_response_samples_ms : []), elapsed]
    .slice(-20);
  void updateRun(active.runId, {
    transcript_response_samples_ms: samples,
    transcript_response_median_ms: percentile(samples, 0.5),
    transcript_response_p95_ms: percentile(samples, 0.95),
  });
}

export function completeQualificationRun(failedReason?: string) {
  const active = readActiveRun();
  if (!active) return;
  const durationSeconds = Math.max(0, Math.round((Date.now() - active.activatedAt) / 1000));
  void (async () => {
    const { data } = await (supabase as any)
      .from('network_qualification_runs')
      .select('disconnect_count')
      .eq('id', active.runId)
      .maybeSingle();
    await updateRun(active.runId, {
      status: failedReason ? 'failed' : 'session_complete',
      session_duration_seconds: durationSeconds,
      completed_without_disconnect: !failedReason && Number(data?.disconnect_count || 0) === 0,
      last_disconnect_reason: failedReason?.slice(0, 500) || undefined,
      completed_at: new Date().toISOString(),
    });
  })();
  saveActiveRun(null);
}

